/**
 * Módulo de Formulário de Orçamento (Budget)
 * Gerencia seleções, quantitativos e envio para a Forms API.
 */
import {
  loadProductsData,
  getProductThumb,
  assetPath,
  productsData,
  postJson,
  isTurnstileEnabled,
  getTurnstileToken,
  resetTurnstileWidget,
  renderTurnstileWidget,
  showToast,
} from "../app.js";

let budgetFocusTrap = null;
let pendingPreSelect = null;

export const budgetQuantities = {};

function preSelectProductInBudget(productName) {
  const categories = Object.keys(productsData);
  for (const categoryKey of categories) {
    const products = productsData[categoryKey]?.products || [];
    const index = products.findIndex((p) => p.name === productName);
    if (index !== -1) {
      const productId = `${categoryKey}-${index}`;
      if (!budgetQuantities[productId]) {
        budgetQuantities[productId] = {
          selected: true,
          quantity: 1,
          name: productName,
          category: productsData[categoryKey].title || categoryKey,
        };
      } else {
        budgetQuantities[productId].selected = true;
      }
      break;
    }
  }
}

export async function openBudgetModal(preSelectProductName = null) {
  if (preSelectProductName) {
    pendingPreSelect = preSelectProductName;
  }

  const container = document.getElementById("budgetProductsList");
  container.innerHTML = '<div class="budget-skeleton-loader"></div><div class="budget-skeleton-loader"></div><div class="budget-skeleton-loader"></div>';

  document.getElementById("budgetModal").classList.add("active");
  window.modalScrollY = window.scrollY;
  document.body.style.top = `-${window.modalScrollY}px`;
  document.body.classList.add("modal-open");
  document.documentElement.classList.add("modal-open");

  setupFocusTrap(document.getElementById("budgetModal"));

  await loadProductsData();

  if (pendingPreSelect) {
    preSelectProductInBudget(pendingPreSelect);
    pendingPreSelect = null;
  }

  renderBudgetProducts();

  if (isTurnstileEnabled()) {
    renderTurnstileWidget(
      "budget",
      "budgetTurnstile",
      "budgetTurnstileMessage",
    ).catch(() => {
      const messageEl = document.getElementById("budgetTurnstileMessage");
      if (messageEl) {
        messageEl.textContent =
          "Não foi possível carregar a proteção anti-bot.";
        messageEl.className = "form-message error";
        messageEl.style.display = "block";
      }
    });
  }
}

export function closeBudgetModal() {
  document.getElementById("budgetModal").classList.remove("active");
  document.body.classList.remove("modal-open");
  document.documentElement.classList.remove("modal-open");
  document.body.style.top = "";
  window.scrollTo(0, window.modalScrollY || 0);
  updateBudgetCartBadge();

  if (budgetFocusTrap) {
    document.removeEventListener('keydown', budgetFocusTrap);
    budgetFocusTrap = null;
  }
}

function setupFocusTrap(modalEl) {
  const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  const focusableContent = modalEl.querySelectorAll(focusableElements);
  const firstFocusableElement = focusableContent[0];
  const lastFocusableElement = focusableContent[focusableContent.length - 1];

  budgetFocusTrap = function(e) {
    let isTabPressed = e.key === 'Tab' || e.keyCode === 9;
    if (!isTabPressed) return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusableElement) {
        lastFocusableElement.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusableElement) {
        firstFocusableElement.focus();
        e.preventDefault();
      }
    }
  };
  document.addEventListener('keydown', budgetFocusTrap);
  if(firstFocusableElement) firstFocusableElement.focus();
}

export function closeBudgetModalOnOverlay(event) {
  if (event.target.id === "budgetModal") closeBudgetModal();
}

function renderBudgetProducts() {
  const container = document.getElementById("budgetProductsList");
  container.innerHTML = "";

  const categories = {
    thermal: "Impressoras Térmicas",
    mobile: "Coletores de Dados Mobile",
    scanners: "Scanners de Código de Barras",
    extreme: "Extreme Networks - Cloud & APs",
  };

  Object.keys(categories).forEach((categoryKey) => {
    const categoryData = productsData[categoryKey];
    if (!categoryData) return;

    const categoryHeader = document.createElement("div");
    categoryHeader.className = "budget-category-header";
    categoryHeader.textContent = categories[categoryKey];
    container.appendChild(categoryHeader);

    categoryData.products.forEach((product, index) => {
      const productId = `${categoryKey}-${index}`;

      if (!budgetQuantities[productId]) {
        budgetQuantities[productId] = {
          selected: false,
          quantity: 1,
          name: product.name,
          category: categories[categoryKey],
        };
      }

      const thumbUrl = getProductThumb(product.name) || product.image;
      const productItem = document.createElement("div");
      productItem.className = "budget-product-item";

      productItem.innerHTML = `
        <input type="checkbox" class="budget-product-checkbox" id="check-${productId}"
          ${budgetQuantities[productId].selected ? "checked" : ""} onchange="toggleProductSelection('${productId}')">
        <div class="budget-product-thumb">
          ${thumbUrl ? `<img src="${assetPath(thumbUrl)}" alt="${product.name}" class="budget-product-image" loading="lazy">` : `<span class="budget-product-icon">${product.icon}</span>`}
        </div>
        <div class="budget-product-info">
          <div class="budget-product-name">${product.name}</div>
          <div class="budget-product-category">${product.subtitle}</div>
        </div>
        <div class="budget-product-quantity">
          <button type="button" class="budget-qty-btn" onclick="updateQuantity('${productId}', -1)" ${!budgetQuantities[productId].selected || budgetQuantities[productId].quantity <= 1 ? "disabled" : ""}>−</button>
          <input type="number" class="budget-qty-input" id="qty-${productId}" value="${budgetQuantities[productId].quantity}" min="1" max="999" ${!budgetQuantities[productId].selected ? "disabled" : ""} onchange="setQuantity('${productId}', this.value)">
          <button type="button" class="budget-qty-btn" onclick="updateQuantity('${productId}', 1)" ${!budgetQuantities[productId].selected || budgetQuantities[productId].quantity >= 999 ? "disabled" : ""}>+</button>
        </div>
      `;
      container.appendChild(productItem);
    });
  });
}

export function updateBudgetCartBadge() {
  const count = Object.values(budgetQuantities).filter((p) => p.selected).length;
  const btn = document.getElementById('budgetCartFloat');
  const badge = document.getElementById('budgetCartBadge');
  if (!btn || !badge) return;
  badge.textContent = String(count);
  if (count > 0) {
    btn.classList.add('visible');
  } else {
    btn.classList.remove('visible');
  }
}

export function toggleProductSelection(productId) {
  budgetQuantities[productId].selected = !budgetQuantities[productId].selected;
  renderBudgetProducts();
  updateBudgetCartBadge();

  if (budgetQuantities[productId].selected) {
    showToast(`${budgetQuantities[productId].name} adicionado ao orçamento.`);
    announceToScreenReader(`${budgetQuantities[productId].name} adicionado ao orçamento.`);
  } else {
    announceToScreenReader(`${budgetQuantities[productId].name} removido do orçamento.`);
  }
}

function announceToScreenReader(message) {
  let announcer = document.getElementById('aria-announcer');
  if (!announcer) {
    announcer = document.createElement('div');
    announcer.id = 'aria-announcer';
    announcer.setAttribute('aria-live', 'polite');
    announcer.style.position = 'absolute';
    announcer.style.width = '1px';
    announcer.style.height = '1px';
    announcer.style.margin = '-1px';
    announcer.style.overflow = 'hidden';
    announcer.style.clip = 'rect(0, 0, 0, 0)';
    document.body.appendChild(announcer);
  }
  announcer.textContent = message;
}

export function updateQuantity(productId, delta) {
  const newQty = budgetQuantities[productId].quantity + delta;
  if (newQty >= 1 && newQty <= 999) {
    budgetQuantities[productId].quantity = newQty;
    renderBudgetProducts();
    announceToScreenReader(`Quantidade atualizada para ${newQty}.`);
  }
}

export function setQuantity(productId, value) {
  const newQty = Math.max(
    1,
    Math.min(999, parseInt(value) || 1),
  );
  budgetQuantities[productId].quantity = newQty;
  renderBudgetProducts();
  announceToScreenReader(`Quantidade atualizada para ${newQty}.`);
}

export async function handleBudgetSubmit(event) {
  event.preventDefault();

  const name = document.getElementById("budget-name").value;
  const email = document.getElementById("budget-email").value;
  const phone = document.getElementById("budget-phone").value;
  const company = document.getElementById("budget-company").value;
  const notes = document.getElementById("budget-notes").value;
  const website = document.getElementById("budget-website")?.value || "";
  const turnstileToken = getTurnstileToken("budget");
  const modeEl = document.querySelector('input[name="budget-mode"]:checked');
  const modeLabel = modeEl && modeEl.value === "locacao" ? "Locação" : "Compra";

  const selectedProducts = Object.keys(budgetQuantities)
    .filter((key) => budgetQuantities[key].selected)
    .map((key) => ({
      name: budgetQuantities[key].name,
      category: budgetQuantities[key].category,
      quantity: budgetQuantities[key].quantity,
    }));

  if (selectedProducts.length === 0) {
    const messageEl = document.getElementById("budgetFormMessage");
    messageEl.textContent = "Por favor, selecione pelo menos um produto.";
    messageEl.className = "form-message error";
    messageEl.style.display = "block";
    setTimeout(() => {
      messageEl.style.display = "none";
    }, 3000);
    return;
  }

  const submitBtn = document.getElementById("budgetSubmitBtn");
  const originalBtnText = submitBtn.textContent;

  if (isTurnstileEnabled() && !turnstileToken) {
    const messageEl = document.getElementById("budgetTurnstileMessage");
    if (messageEl) {
      messageEl.textContent =
        "Confirme a verificação anti-bot antes de enviar.";
      messageEl.className = "form-message error";
      messageEl.style.display = "block";
    }
    return;
  }

  submitBtn.disabled = true;
  submitBtn.innerHTML = '<span class="btn-spinner"></span> Enviando...';

  try {
    await postJson("/api/budget", {
      name,
      email,
      phone,
      company,
      mode: modeLabel === "Locação" ? "locacao" : "compra",
      notes,
      products: selectedProducts,
      website,
      turnstileToken,
    });

    const messageEl = document.getElementById("budgetFormMessage");
    messageEl.textContent =
      "Solicitação enviada com sucesso! Retornaremos em breve.";
    messageEl.className = "form-message success";
    messageEl.style.display = "block";
    submitBtn.disabled = false;
    submitBtn.textContent = originalBtnText;

    setTimeout(() => {
      messageEl.style.display = "none";
      closeBudgetModal();
      document.getElementById("budgetForm").reset();
      resetTurnstileWidget("budget");
    }, 2000);
  } catch (err) {
    const messageEl = document.getElementById("budgetFormMessage");
    messageEl.textContent =
      err.message || "Não foi possível enviar seu orçamento agora.";
    messageEl.className = "form-message error";
    messageEl.style.display = "block";
    resetTurnstileWidget("budget");
    submitBtn.disabled = false;
    submitBtn.textContent = originalBtnText;
  }
}
